import React, { useContext, useState } from "react";
import { CartContext } from "../Component/CartContext";
import productData from "../Datas/productData";
import "../css/cartpage.scss";
import { Link, useNavigate } from "react-router-dom";
import Footer from "./Footer";
import Header from "./Header";

const CartPage = () => {
  const navigate = useNavigate();
  const { cartItems, removeFromCart, updateQuantity } = useContext(CartContext);
  const [promoCode, setPromoCode] = useState("");

  const getProductById = (id) => {
    return productData.find((product) => product.id === id);
  };

  const calculateSubtotal = () => {
    let subtotal = 0;
    cartItems.forEach((item) => {
      const product = getProductById(item.id);
      if (product) {
        subtotal += parseFloat(product.price) * item.quantity;
      }
    });
    return subtotal;
  };

  const subtotal = calculateSubtotal();
  const total = subtotal;

  const handleUpdateQuantity = (id, size, color, newQuantity) => {
    if (newQuantity > 0) {
      updateQuantity(id, size, color, newQuantity);
    }
  };

  const handleRemoveItem = (id, size, color) => {
    removeFromCart(id, size, color);
  };

  const handleCheckout = () => {
    if (cartItems.length > 0) {
      navigate("/checkout");
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleApplyPromo = () => {
    if (promoCode.trim()) {
      alert(`Promo code "${promoCode}" applied!`);
    }
  };

  const toTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (cartItems.length === 0) {
    return (
      <div className="cart-page">
        <Header />
        <div className="cart-page__empty">
          <div className="cart-page__empty-box">
            <div className="empty-icon">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              >
                <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
                <line x1="3" y1="6" x2="21" y2="6" />
                <path d="M16 10a4 4 0 0 1-8 0" />
              </svg>
            </div>
            <h2>Your cart is empty</h2>
            <p>Looks like you haven't added anything to your cart yet.</p>
            <Link
              to="/shop"
              className="cart-page__continue-btn"
              onClick={toTop}
            >
              Continue Shopping
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="cart-page">
      <Header />

      <div className="cart-page__container">
        <div className="cart-page__header">
          <h1>Shopping Cart</h1>
          <span className="cart-count">
            {cartItems.length} {cartItems.length === 1 ? "item" : "items"}
          </span>
        </div>

        <div className="cart-page__content">
          {/* Cart Items */}
          <div className="cart-page__items">
            <div className="cart-items-header">
              <span className="col-product">Product</span>
              <span className="col-price">Price</span>
              <span className="col-qty">Quantity</span>
              <span className="col-total">Total</span>
            </div>

            {cartItems.map((item, index) => {
              const product = getProductById(item.id);
              if (!product) return null;

              const itemTotal = parseFloat(product.price) * item.quantity;

              return (
                <div
                  key={`${item.id}-${item.size}-${item.color}-${index}`}
                  className="cart-item"
                >
                  <div className="cart-item__product">
                    <div className="cart-item__image">
                      <img src={product.image} alt={product.name} />
                    </div>
                    <div className="cart-item__details">
                      <h3 className="cart-item__name">{product.name}</h3>
                      <p className="cart-item__meta">
                        {item.color && <span>Color: {item.color}</span>}
                        {item.color && item.size && " / "}
                        {item.size && <span>Size: {item.size}</span>}
                      </p>
                      <p className="cart-item__category">{product.category}</p>
                    </div>
                  </div>

                  <div className="cart-item__price">
                    HK${parseFloat(product.price).toLocaleString("en-US")}
                  </div>

                  <div className="cart-item__qty">
                    <div className="qty-controls">
                      <button
                        className="qty-btn"
                        onClick={() =>
                          handleUpdateQuantity(
                            item.id,
                            item.size,
                            item.color,
                            item.quantity - 1
                          )
                        }
                      >
                        -
                      </button>
                      <span className="qty-value">{item.quantity}</span>
                      <button
                        className="qty-btn"
                        onClick={() =>
                          handleUpdateQuantity(
                            item.id,
                            item.size,
                            item.color,
                            item.quantity + 1
                          )
                        }
                      >
                        +
                      </button>
                    </div>
                    <button
                      className="remove-btn"
                      onClick={() =>
                        handleRemoveItem(item.id, item.size, item.color)
                      }
                    >
                      Remove
                    </button>
                  </div>

                  <div className="cart-item__total">
                    HK${itemTotal.toLocaleString("en-US")}
                  </div>
                </div>
              );
            })}

            <div className="cart-items-footer">
              <Link to="/shop" className="continue-shopping" onClick={toTop}>
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="19" y1="12" x2="5" y2="12" />
                  <polyline points="12 19 5 12 12 5" />
                </svg>
                Continue Shopping
              </Link>
            </div>
          </div>

          {/* Order Summary */}
          <div className="cart-page__summary">
            <div className="summary-box">
              <h2>Order Summary</h2>

              <div className="summary-row">
                <span>Subtotal</span>
                <span>HK${subtotal.toLocaleString("en-US")}</span>
              </div>

              <div className="summary-row">
                <span>Shipping</span>
                <span>
                  <span className="free-shipping">FREE</span>
                </span>
              </div>

              <div className="summary-divider"></div>

              <div className="summary-row total">
                <span>Total</span>
                <span>HK${total.toLocaleString("en-US")}</span>
              </div>

              <button className="checkout-btn" onClick={handleCheckout}>
                Proceed to Checkout
              </button>

              <div className="secure-badge">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
                <span>Secure Checkout</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CartPage;
